/**
 * When `rfq.document.description` is shorter than this we treat it as too
 * skimpy for the quote card and prefer the parsed "Product Description"
 * section from the RFQ markdown summary instead. Shared with the helper below
 * so the "what to show" and "what to strip" rules can't drift apart.
 */
export const MIN_DESCRIPTION_LENGTH = 100;

/**
 * Result of parsing a "Product Description" section out of an RFQ markdown
 * document.
 */
export interface ExtractedProductDescription {
  /**
   * Plain-text description with markdown formatting stripped. Null when no
   * recognizable section is found.
   */
  description: string | null;
  /**
   * Original markdown with the matched Product Description block removed. When
   * no section is matched this is the original markdown unchanged (or an empty
   * string if no markdown was provided), so callers can always pass the result
   * straight to a markdown renderer.
   */
  markdownWithoutDescription: string;
}

/**
 * Extracts the "Product Description" section from an RFQ markdown document and
 * also returns the markdown with that section spliced out.
 *
 * The rfq_writer prompt produces markdown that mixes a few label styles:
 *   - `## Product Description\n<text>` — heading followed by one or more paragraphs
 *   - `**Product Description:** <text>` — bold inline label on a single line
 *   - `Product Description: <text>` — plain inline label on a single line
 *
 * `description` is null when no recognizable section is found so callers can
 * fall back to a different description source.
 */
export default function extractProductDescription(
  markdown: string | null | undefined
): ExtractedProductDescription {
  if (!markdown) {
    return { description: null, markdownWithoutDescription: '' };
  }

  const lines = markdown.split(/\r?\n/);

  // Permits the closing `**` to appear before, after, or interleaved with the
  // colon/dash, e.g. `**Product Description:**`, `**Product Description**:`,
  // `## Product Description`, or plain `Product Description: …`.
  const labelPattern =
    /^\s*(?:#{1,6}\s+)?(?:\*\*)?Product\s+Description(?:\*\*|[:\-—])*\s*(.*)$/i;
  const nextSectionPattern = /^\s*(?:#{1,6}\s+|\*\*[^*\n]+\*\*\s*:?)/;

  let startIdx = -1;
  let firstLineRest = '';

  for (let i = 0; i < lines.length; i++) {
    const match = lines[i].match(labelPattern);

    if (match) {
      startIdx = i;
      firstLineRest = match[1] ?? '';
      break;
    }
  }

  if (startIdx === -1) {
    return { description: null, markdownWithoutDescription: markdown };
  }

  const collected: string[] = [];

  if (firstLineRest.trim()) {
    collected.push(firstLineRest);
  }

  let endIdxExclusive = lines.length;

  for (let j = startIdx + 1; j < lines.length; j++) {
    const line = lines[j];

    if (nextSectionPattern.test(line)) {
      endIdxExclusive = j;
      break;
    }
    collected.push(line);
  }

  const cleaned = stripMarkdown(collected.join('\n')).trim();

  const beforeLines = lines.slice(0, startIdx);
  const afterLines = lines.slice(endIdxExclusive);
  const markdownWithoutDescription = [...beforeLines, ...afterLines]
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/^\s+|\s+$/g, '');

  return {
    description: cleaned || null,
    markdownWithoutDescription
  };
}

/**
 * Minimal shape of an RFQ needed to decide what to show in the description
 * surfaces. Defined structurally so callers can pass either the
 * `frontend/src/shared/types/product` RFQ or the `frontend/src/shared/types/rfq`
 * variant without coupling this util to a specific type module.
 */
export interface RfqForDisplay {
  rfq_markdown?: string | null;
  document?: { description?: string | null } | null;
}

/**
 * Returns the markdown that should be rendered in the RFQ summary tab and the
 * RFQ slide-over drawer. The "Product Description" section is only spliced out
 * when the quote card is actually displaying the extracted version — i.e. when
 * `rfq.document.description` is too short to take precedence AND
 * `extractProductDescription` found a recognizable section in the markdown.
 *
 * In every other case the original markdown is returned unchanged so we don't
 * delete content the user still needs to see.
 */
export function getRfqMarkdownForDisplay(
  rfq: RfqForDisplay | null | undefined
): string {
  const original = rfq?.rfq_markdown ?? '';
  const documentDescription = rfq?.document?.description?.trim() ?? '';

  if (documentDescription.length >= MIN_DESCRIPTION_LENGTH) {
    return original;
  }

  const { description, markdownWithoutDescription } =
    extractProductDescription(original);

  return description !== null ? markdownWithoutDescription : original;
}

function stripMarkdown(input: string): string {
  return (
    input
      .replace(/`([^`]+)`/g, '$1')
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/__([^_]+)__/g, '$1')
      .replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, '$1')
      .replace(/(?<!_)_([^_\n]+)_(?!_)/g, '$1')
      .replace(/^\s*[-*+]\s+/gm, '')
      // Strip any stray bold markers left over from unbalanced/partial matches
      // (e.g. an orphan `**` at the start of the line).
      .replace(/\*\*/g, '')
      .replace(/\n{3,}/g, '\n\n')
  );
}
